import { Button } from "./ui/button";
import { Input } from "./ui/input";
import SearchableSelect from "./SearchableSelect";

export const VENICE_LABEL = "Venice";
export const VENICE_PIAZZALE = "Venice - Piazzale Roma";
export const VENICE_AIRPORT = "Venice Airport";
export const VENICE_NEAR = "Venice - Near Venice";
export const VENICE_WATER_TAXI = "Venice - Water Taxi";
export const VENICE_HOTEL = "Venice - Hotel";

/**
 * Returns true if the given location label refers to any Venice variant
 * (collapsed placeholder, Piazzale Roma, Airport, Near Venice, Water Taxi or Hotel).
 */
export const isVeniceVariant = (loc) =>
  loc === VENICE_LABEL ||
  loc === VENICE_PIAZZALE ||
  loc === VENICE_AIRPORT ||
  loc === VENICE_NEAR ||
  loc === VENICE_WATER_TAXI ||
  loc === VENICE_HOTEL;

/**
 * Returns the underlying DB location for matching routes — every Venice
 * variant shares the same price as Piazzale Roma.
 */
export const venicedbKey = (loc) =>
  loc === VENICE_AIRPORT || loc === VENICE_LABEL || loc === VENICE_NEAR || loc === VENICE_WATER_TAXI || loc === VENICE_HOTEL
    ? VENICE_PIAZZALE
    : loc;

/**
 * Display label for dropdowns: any specific Venice variant collapses to "Venice".
 */
export const veniceDisplayLabel = (loc) =>
  isVeniceVariant(loc) ? VENICE_LABEL : loc;

/**
 * Inline sub-picker: appears only when the parent value is a Venice variant.
 * Renders pill buttons (Piazzale Roma / Airport / Near Venice / + Water Taxi).
 * The 4th button ("Water Taxi") only renders when `showWaterTaxi` is true
 * (gated by admin booking-settings `water_taxi_enabled`).
 *
 * When "Near Venice" or "Water Taxi" is selected, the parent component should
 * show an additional address text input. The `nearAddress`/`onChangeNearAddress`
 * props handle the address for whichever address-requiring variant is active.
 *
 * Props:
 *   value: current full label
 *   onSelect: (fullLabel) => void
 *   testIdPrefix: e.g. "origin"
 *   label: optional caption
 *   nearAddress: current specific address text (optional, for inline mode)
 *   onChangeNearAddress: (text) => void (optional, for inline mode)
 *   disabledVariant: variant to disable (e.g. Venice→Venice cross-disable)
 *   showWaterTaxi: render the 4th "+ Water Taxi" option
 */
export default function VeniceSubPicker({
  value,
  onSelect,
  testIdPrefix = "venice",
  label = "Choose pickup point",
  nearAddress,
  onChangeNearAddress,
  disabledVariant,
  showWaterTaxi = false,
  // ------ Hotel-in-Venice props ------
  hotelOptions = [],           // [{hotel_id, name, slug, airport_price, surcharge}]
  selectedHotelId,             // currently picked hotel_id
  onSelectHotel,               // (hotel_id) => void
  showHotel = true,            // gate the button per parent form logic
  hideAirportButton = false,   // used by the "TO" picker when FROM is Airport (Airport→Airport is nonsense)
}) {
  if (!isVeniceVariant(value)) return null;

  const isPiazzale = value === VENICE_PIAZZALE;
  const isAirport = value === VENICE_AIRPORT;
  const isNear = value === VENICE_NEAR;
  const isWaterTaxi = value === VENICE_WATER_TAXI;
  const isHotel = value === VENICE_HOTEL;

  const isDisabled = (variant) => disabledVariant && variant === disabledVariant;

  const btn = (active, disabled) =>
    "flex-1 rounded-full border text-sm transition-colors " +
    (disabled
      ? "bg-transparent text-white/20 border-white/10 cursor-not-allowed line-through"
      : active
        ? "bg-[#C5A059] text-black border-[#C5A059] hover:bg-[#C5A059]"
        : "bg-transparent text-white/80 border-white/20 hover:bg-white/5");

  const safeSelect = (variant) => {
    if (isDisabled(variant)) return;
    onSelect(variant);
  };

  // Each variant that requires a custom address gets its own placeholder + sub-line copy.
  const addressUiByVariant = {
    [VENICE_NEAR]: {
      placeholder: "Address / hotel within 15 km of Venice (accessible by car)",
      subtitle: "Address / hotel within 15 km of Venice (accessible by car).",
    },
    [VENICE_WATER_TAXI]: {
      placeholder: "Hotel name or address in Venice City Center",
      subtitle: "Address / hotel in Venice City Center (accessible by Boat).",
    },
  };
  const addressUi = addressUiByVariant[value];

  return (
    <div className="mt-2" data-testid={`${testIdPrefix}-venice-subpicker`}>
      <p className="text-white/60 text-xs mb-2">{label}</p>
      <div className="flex gap-2 flex-wrap">
        {!hideAirportButton && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => safeSelect(VENICE_AIRPORT)}
            className={btn(isAirport, isDisabled(VENICE_AIRPORT))}
            disabled={isDisabled(VENICE_AIRPORT)}
            data-testid={`${testIdPrefix}-venice-airport`}
          >
            Airport
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          onClick={() => safeSelect(VENICE_PIAZZALE)}
          className={btn(isPiazzale, isDisabled(VENICE_PIAZZALE))}
          disabled={isDisabled(VENICE_PIAZZALE)}
          data-testid={`${testIdPrefix}-venice-piazzale`}
        >
          Piazzale Roma
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => safeSelect(VENICE_NEAR)}
          className={btn(isNear, isDisabled(VENICE_NEAR))}
          disabled={isDisabled(VENICE_NEAR)}
          data-testid={`${testIdPrefix}-venice-near`}
        >
          Near Venice
        </Button>
        {showWaterTaxi && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => safeSelect(VENICE_WATER_TAXI)}
            className={btn(isWaterTaxi, isDisabled(VENICE_WATER_TAXI))}
            disabled={isDisabled(VENICE_WATER_TAXI)}
            data-testid={`${testIdPrefix}-venice-water-taxi`}
          >
            + Water Taxi
          </Button>
        )}
        {showHotel && hotelOptions.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => safeSelect(VENICE_HOTEL)}
            className={btn(isHotel, isDisabled(VENICE_HOTEL))}
            disabled={isDisabled(VENICE_HOTEL)}
            data-testid={`${testIdPrefix}-venice-hotel`}
          >
            Hotel in Venice
          </Button>
        )}
      </div>
      {(isNear || isWaterTaxi) && typeof onChangeNearAddress === "function" && addressUi && (
        <div className="mt-2" data-testid={`${testIdPrefix}-${isWaterTaxi ? "water-taxi" : "near-venice"}-address-wrap`}>
          <Input
            value={nearAddress || ""}
            onChange={(e) => onChangeNearAddress(e.target.value)}
            placeholder={addressUi.placeholder}
            className="input-dark"
            maxLength={300}
            data-testid={`${testIdPrefix}-${isWaterTaxi ? "water-taxi" : "near-venice"}-address`}
          />
          <p className="text-white/40 text-[10px] mt-1">
            {addressUi.subtitle}
          </p>
        </div>
      )}
      {isHotel && typeof onSelectHotel === "function" && (
        <div className="mt-2" data-testid={`${testIdPrefix}-hotel-select-wrap`}>
          <SearchableSelect
            value={selectedHotelId || ""}
            onValueChange={(v) => onSelectHotel(v || null)}
            placeholder="— Pick a hotel —"
            triggerTestId={`${testIdPrefix}-hotel-select`}
            ariaLabel="Search hotel in Venice"
            emptyMessage="No hotel matches"
            items={hotelOptions.map((h) => ({
              value: h.hotel_id,
              label: h.name,
              testId: `${testIdPrefix}-hotel-option-${h.slug || h.hotel_id}`,
            }))}
          />
          <p className="text-white/40 text-[10px] mt-1">
            Airport ↔ hotel is a fixed all-in price. <strong className="text-[#C5A059]">Water taxi + meet &amp; greet included</strong>.
          </p>
        </div>
      )}
    </div>
  );
}
